import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, Dialog, Drawer, DialogContent, DialogTitle } from "@mui/material";
import OrderTiming from "./OrderTiming";
import { useKitchenNow } from "./TimedOrderCard";
import { formatReceivedTime, getOrderTiming } from "../orderTiming";
import { isLiveTask } from "../kitchenActions";
import { preparationNoteKey } from "../preparationComments";
import { kitchenOrderPeople, tablePendingJobs } from "../tableWorkload";
import "./TableOrderDetails.css";

const entryFor = (job, task) => ({
  key: preparationNoteKey(job.jobId, task.taskId), orderId: job.jobId,
  taskId: task.taskId, jobStatus: job.status, status: task.status || job.status, quantity: task.quantity,
});

const addonEntries = (task) => (task.additionalProperties?.addons || []).map((addon) => typeof addon === "string"
  ? { name: addon, quantity: 1 }
  : { name: addon?.itemName || addon?.name || addon?.title || addon?.addonName, quantity: Number(addon?.quantity) || 1 })
  .filter((addon) => addon.name);

// Items marked ready stay listed (ticked and struck out) instead of disappearing.
const isDoneTask = (task) => !!task && (task.taskStatus === "COMPLETED" || task.status === "COMPLETED");

// Merge tasks marked ready in this drawer back into the refreshed jobs, unless they are live again.
const withMarkedReady = (jobs, marked) => {
  if (!marked.length) return jobs;
  const result = jobs.map((job) => ({ ...job, tasks: [...(job.tasks || [])] }));
  marked.forEach(({ job, task }) => {
    let target = result.find((candidate) => candidate.jobId === job.jobId);
    if (!target) {
      target = { ...job, status: "COMPLETED", tasks: [] };
      result.push(target);
    }
    if (target.tasks.some((candidate) => candidate.taskId === task.taskId && isLiveTask(candidate))) return;
    target.tasks = target.tasks.filter((candidate) => candidate.taskId !== task.taskId);
    target.tasks.push({ ...task, status: "COMPLETED", taskStatus: "COMPLETED" });
  });
  return result;
};

export default function TableOrderDetails({
  open, table, busy, loading, error, onRetry, onClose, onStart, onReady, onEdit, onComment,
  notes = {}, notesLoading, notesError, onReloadNotes,
  presentation = "dialog",
  onAcceptOrders, onRejectOrders, actionBusy = false, allowAccept = true, allowReject = true,
  tablePendingActions = false, activeAction = null,
}) {
  const now = useKitchenNow();
  const [selection, setSelection] = useState(new Set());
  const [confirmation, setConfirmation] = useState(null);
  const actionRef = useRef(false);
  const jobs = table?.jobs || [];
  const pendingIds = tablePendingJobs(table).map((job) => job?.jobId);
  const invokeDecision = async (handler, ...args) => {
    try { await handler?.(...args); }
    catch (_) { /* The action reports its error without unmounting the drawer. */ }
  };
  const liveReady = jobs.flatMap((job) => job.status === "IN_PROGRESS"
    ? (job.tasks || []).filter((task) => task.taskId && isLiveTask(task) &&
      (!task.status || task.status === "IN_PROGRESS")).map((task) => entryFor(job, task))
    : []);
  const validKeys = JSON.stringify(liveReady.map((entry) => entry.key));
  useEffect(() => {
    const valid = new Set(JSON.parse(validKeys));
    setSelection((previous) => new Set([...previous].filter((key) => valid.has(key))));
    setConfirmation(null);
  }, [validKeys, table?.key]);
  const selected = liveReady.filter((entry) => selection.has(entry.key));
  const selectedQty = selected.reduce((sum, entry) => sum + Number(entry.quantity || 0), 0);
  // The live workload only returns pending/processing tasks, so remember what was marked ready here.
  const [markedReady, setMarkedReady] = useState([]);
  useEffect(() => { setMarkedReady([]); }, [table?.key]);
  const displayJobs = withMarkedReady(jobs, markedReady);
  const run = async () => {
    if (actionRef.current || busy || !confirmation) return;
    actionRef.current = true;
    try {
      const marking = confirmation.kind === "ready" ? selected.map((entry) => {
        const job = jobs.find((candidate) => candidate.jobId === entry.orderId);
        return { job, task: job?.tasks?.find((candidate) => candidate.taskId === entry.taskId) };
      }).filter((value) => value.task) : [];
      const result = await (confirmation.kind === "start" ? onStart(confirmation.entries) : onReady(selected));
      const succeeded = result?.succeeded ? new Set(result.succeeded) : null;
      const done = marking.filter(({ job }) => !succeeded || succeeded.has(job.jobId));
      if (done.length) setMarkedReady((previous) => [...previous, ...done]);
      setSelection(new Set());
      setConfirmation(null);
    } catch (_) {
      // The shared controller reports the actual failure and preserves selection.
    } finally { actionRef.current = false; }
  };
  const isDrawer = presentation === "drawer";
  const Container = isDrawer ? Drawer : Dialog;
  const containerProps = isDrawer ? { anchor: "right" } : { fullWidth: true, maxWidth: "md" };
  return (
    <Container {...containerProps} open={!!open} onClose={busy ? undefined : onClose}
      aria-labelledby="kds-table-details-title" PaperProps={{
        className: `kds-table-details${isDrawer ? " kds-table-details--drawer" : ""}`,
        role: "dialog", "aria-modal": true, "aria-labelledby": "kds-table-details-title",
      }}>
      <DialogTitle id="kds-table-details-heading">
        <div className="kds-table-details-heading">
          <span id="kds-table-details-title">{table?.name ? `Table ${table.name}` : "Kitchen order details"}</span>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close table order details">Close</button>
        </div>
      </DialogTitle>
      <DialogContent>
        <p className="kds-table-scope">Kitchen orders matching the current date, search, source, slot and station filters.</p>
        {loading && <p role="status">Updating kitchen orders…</p>}
        {error && <div role="alert" className="kds-detail-error">
          {error} <button type="button" onClick={onRetry}>Retry orders</button>
        </div>}
        {notesLoading && <p role="status">Loading saved preparation comments…</p>}
        {notesError && <div role="alert" className="kds-detail-error">
          {notesError} <button type="button" onClick={onReloadNotes}>Retry comments</button>
        </div>}
        {!displayJobs.length && !loading && !error && <p>No active kitchen orders for this table under the current filters.</p>}
        {tablePendingActions && pendingIds.length > 0 && (
          <div className="kds-detail-actions" aria-label="Pending table order actions">
            <span>All pending orders at this table</span>
            <button type="button" disabled={actionBusy || loading || !allowAccept || typeof onAcceptOrders !== "function"}
              onClick={() => invokeDecision(onAcceptOrders, pendingIds, table)}>
              {actionBusy && activeAction?.tableKey === table?.key && activeAction?.status === "IN_PROGRESS"
                ? <><CircularProgress size={12} color="inherit" aria-label="Accepting table orders" /> Accepting…</> : "Accept new orders"}
            </button>
            <button type="button" className="kds-detail-reject" disabled={actionBusy || loading || !allowReject || typeof onRejectOrders !== "function"}
              onClick={() => invokeDecision(onRejectOrders, pendingIds, table)}>
              {actionBusy && activeAction?.tableKey === table?.key && activeAction?.status === "REJECTED"
                ? <><CircularProgress size={12} color="inherit" aria-label="Rejecting table orders" /> Rejecting…</> : "Reject new orders"}
            </button>
          </div>
        )}
        {displayJobs.map((job) => (
          <article className="kds-table-detail-order" key={job.jobId}>
            <h3>Job {job.jobId} · KOT #{job.token ?? "—"}</h3>
            <p>Ordered {formatReceivedTime(job.created)} · {job.status === "PENDING" ? "Pending" : job.status === "COMPLETED" ? "Ready" : "Processing"}</p>
            <p className="kds-table-order-people">
              <span>Captain: {kitchenOrderPeople(job).captain || "Not available"}</span>
              <span>Customer: {kitchenOrderPeople(job).customer || "Not available"}</span>
              <span>{job.source || "POS"}</span>
            </p>
            <OrderTiming timing={getOrderTiming({ created: job.created, prepTime: job.metadata?.avgPrepTime }, now)} />
            {job.metadata?.comments && <p className="kds-detail-note">Order comment: {job.metadata.comments}</p>}
            <div className="kds-detail-actions">
              {!tablePendingActions && job.status === "PENDING" && typeof onAcceptOrders === "function" && (
                <>
                  <button type="button" disabled={actionBusy || loading || !allowAccept}
                    onClick={() => invokeDecision(onAcceptOrders, [job.jobId])}>Accept order</button>
                  <button type="button" className="kds-detail-reject" disabled={actionBusy || loading || !allowReject || typeof onRejectOrders !== "function"}
                    onClick={() => invokeDecision(onRejectOrders, [job.jobId])}>Reject order</button>
                </>
              )}
              {!tablePendingActions && job.status === "PENDING" && typeof onAcceptOrders !== "function" && (
                <button type="button" disabled={busy || loading} onClick={() => setConfirmation({
                  kind: "start", entries: (job.tasks || []).filter((task) => task.taskId && isLiveTask(task)).map((task) => entryFor(job, task)),
                })}>Move order to processing</button>
              )}
              {job.status !== "COMPLETED" &&
                <button type="button" disabled={busy || loading} onClick={() => onEdit(job)}>Remove items / edit quantities</button>}
              {job.status === "IN_PROGRESS" && (
                <button type="button" disabled={busy || loading}
                  onClick={() => setSelection((previous) => new Set([
                    ...previous, ...liveReady.filter((entry) => entry.orderId === job.jobId).map((entry) => entry.key),
                  ]))}>Select all processing items</button>
              )}
            </div>
            {(job.tasks || []).filter((task) => isLiveTask(task) || isDoneTask(task)).map((task, index) => {
              const key = preparationNoteKey(job.jobId, task.taskId);
              const ready = liveReady.some((entry) => entry.key === key);
              const done = isDoneTask(task);
              const note = Object.prototype.hasOwnProperty.call(notes, key) ? notes[key] : task.metadata?.comments;
              return (
                <div className={`kds-detail-item${done ? " kds-detail-item--done" : ""}`} key={task.taskId || index}>
                  <label>
                    {done && <span className="kds-detail-item-tick" role="img" aria-label={`${task.title} is ready`}>✓</span>}
                    {ready && <input type="checkbox" checked={selection.has(key)} disabled={busy || loading}
                      onChange={(event) => setSelection((prev) => {
                        const next = new Set(prev);
                        if (event.target.checked) next.add(key); else next.delete(key);
                        return next;
                      })} aria-label={`Select ${task.title} from job ${job.jobId} ready`} />}
                    <strong>{task.title}</strong> <span className="kds-detail-qty">(x{task.quantity})</span>
                  </label>
                  {addonEntries(task).length > 0 && <p>Add-ons: {addonEntries(task).map((addon, addonIndex) => (
                    <React.Fragment key={`${addon.name}-${addonIndex}`}>
                      {addonIndex > 0 && ", "}
                      {addon.name}{addon.quantity > 1 && <> <span className="kds-detail-qty">(x{addon.quantity})</span></>}
                    </React.Fragment>
                  ))}</p>}
                  {note && <p className="kds-detail-note">Preparation comment: {note}</p>}
                  {!done && <button type="button" disabled={busy || loading || notesLoading || !task.taskId}
                    onClick={() => onComment(job, task)}>{note ? "Edit preparation comment" : "Add preparation comment"}</button>}
                </div>
              );
            })}
          </article>
        ))}
        {selected.length > 0 && <div className="kds-detail-actions">
          <button type="button" disabled={busy || loading} onClick={() => setConfirmation({ kind: "ready" })}>
            Mark selected ready ({selectedQty} portions)
          </button>
        </div>}
        {confirmation && <div className="kds-detail-confirm" role="group" aria-label="Confirm kitchen action">
          <p>{confirmation.kind === "start"
            ? "Move the entire order to processing, including all its dishes?"
            : `Mark ${selectedQty} selected portions from ${new Set(selected.map((entry) => entry.orderId)).size} orders ready?`}</p>
          <button type="button" onClick={run} disabled={busy || loading || (confirmation.kind === "ready" && !selected.length)}>
            {busy ? "Updating…" : "Confirm"}
          </button>
          <button type="button" onClick={() => setConfirmation(null)} disabled={busy}>Cancel</button>
        </div>}
      </DialogContent>
    </Container>
  );
}

export function PreparationCommentEditor({ target, busy, error, onClose, onSave }) {
  const [value, setValue] = useState(target.product.comments || "");
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm"
      aria-labelledby="kds-comment-title" PaperProps={{ className: "kds-table-details" }}>
      <DialogTitle id="kds-comment-title">Preparation comment · {target.task.title}</DialogTitle>
      <DialogContent>
        <label className="kds-comment-field">Preparation instructions
          <textarea value={value} onChange={(event) => setValue(event.target.value)} rows={4} disabled={busy} maxLength={2000} />
        </label>
        <p>Saved to this order’s item. Clear the text to remove its comment.</p>
        {error && <p role="alert" className="kds-detail-error">{error}</p>}
        <div className="kds-detail-actions">
          <button type="button" disabled={busy} onClick={() => onSave(value.trim())}>{busy ? "Saving…" : "Save comment"}</button>
          <button type="button" disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
